import { AlertCircle } from "lucide-react";

const GREEN = "#00E676";
const RED = "#EF4444";
const GREY = "#64748B";

export function HitTile({ label, rate, testid }) {
  const pct = rate?.pct;
  const color = pct == null ? "text-slate-400" : pct >= 60 ? "text-[#00E676]" : pct >= 40 ? "text-[#F59E0B]" : "text-[#EF4444]";
  return (
    <div className="rounded-xl border border-[#1E2738] bg-[#0F131D] p-3 text-center" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold leading-none ${color}`}>{pct == null ? "—" : `${pct}%`}</p>
      <p className="mt-1 font-mono text-[11px] text-slate-400">{rate ? `${rate.hits}/${rate.games - rate.pushes}${rate.pushes ? ` · ${rate.pushes} push` : ""}` : "no data"}</p>
    </div>
  );
}

export function AvgTile({ label, value, line, side }) {
  const edge = value != null && line != null ? (side === "under" ? line - value : value - line) : null;
  return (
    <div className="flex items-baseline justify-between border-b border-[#1E2738] py-2 font-mono text-xs">
      <span className="uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-white">
        <span className="text-sm font-bold">{value ?? "—"}</span>
        {edge != null && <span className={`ml-2 ${edge >= 0 ? "text-[#00E676]" : "text-[#EF4444]"}`}>{edge >= 0 ? "+" : ""}{edge.toFixed(1)}</span>}
      </span>
    </div>
  );
}

export function HitChart({ games, line, testid = "prop-hit-chart" }) {
  const data = [...games].reverse();
  const max = Math.max(line * 1.25, ...data.map((g) => g.value)) || 1;
  const mobileStart = Math.max(0, data.length - 10);
  return (
    <div data-testid={testid}>
      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 z-10 border-t border-dashed border-slate-400/70" style={{ bottom: `${Math.round((line / max) * 104) + 16}px` }}>
          <span className="absolute -top-2.5 right-0 rounded bg-[#141A26] px-1.5 font-mono text-[10px] text-slate-300">LINE {line}</span>
        </div>
        <div className="flex h-40 items-end gap-1.5 sm:gap-1.5">
          {data.map((g, i) => {
            const color = g.hit === true ? GREEN : g.hit === false ? RED : GREY;
            const title = `${g.date} ${g.home ? "vs" : "@"} ${g.opp}: ${g.value} (${g.hit === true ? "hit" : g.hit === false ? "miss" : "push"})`;
            return (
              <div key={g.game_id} title={title} aria-label={title} className={`${i < mobileStart ? "hidden sm:flex" : "flex"} flex-1 flex-col items-center justify-end`} data-testid={`prop-bar-${g.game_id}`}>
                <span className="mb-1 font-mono text-[11px] font-bold leading-none sm:text-[10px]" style={{ color }}>{Number.isInteger(g.value) ? g.value : g.value.toFixed(1)}</span>
                <div className="w-full rounded-t-sm transition-[height] duration-500" style={{ height: `${Math.max(4, Math.round((g.value / max) * 104))}px`, backgroundColor: color, opacity: 0.9 }} />
                <span className="mt-1 block h-3 truncate font-mono text-[9px] uppercase leading-3 text-slate-500">{g.home ? "vs" : "@"}{g.opp}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-slate-500">
        <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: GREEN }} /> Hit</span>
        <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: RED }} /> Miss</span>
        <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: GREY }} /> Push</span>
        <span className="ml-auto">Oldest → Most recent</span>
      </div>
    </div>
  );
}

export function GameLog({ games, marketLabel }) {
  return (
    <div className="overflow-x-auto" data-testid="prop-game-log">
      <table className="w-full font-mono text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
            <th className="py-1.5 pr-2">Date</th><th className="py-1.5 pr-2">Opp</th><th className="py-1.5 pr-2">Min</th>
            <th className="py-1.5 pr-2">Pts</th><th className="py-1.5 pr-2">Reb</th><th className="py-1.5 pr-2">Ast</th><th className="py-1.5 pr-2">3PM</th>
            <th className="py-1.5 text-right text-white">{marketLabel}</th>
          </tr>
        </thead>
        <tbody>
          {games.map((g) => (
            <tr key={g.game_id} className="border-t border-[#1E2738] text-slate-300">
              <td className="py-1.5 pr-2 whitespace-nowrap">{g.date?.slice(5)}</td>
              <td className="py-1.5 pr-2">{g.home ? "vs" : "@"} {g.opp}</td>
              <td className="py-1.5 pr-2">{g.min}</td>
              <td className="py-1.5 pr-2">{g.pts}</td><td className="py-1.5 pr-2">{g.reb}</td><td className="py-1.5 pr-2">{g.ast}</td><td className="py-1.5 pr-2">{g.fg3m}</td>
              <td className={`py-1.5 text-right font-bold ${g.hit === true ? "text-[#00E676]" : g.hit === false ? "text-[#EF4444]" : "text-slate-400"}`}>{g.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Unavailable({ reason, tier }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-[#2A364F] bg-[#0F131D] p-4" data-testid="prop-stats-unavailable">
      <AlertCircle size={16} className="mt-0.5 shrink-0 text-[#F59E0B]" />
      <div>
        <p className="font-display text-xs font-bold uppercase tracking-wider text-[#F59E0B]">Statistics unavailable</p>
        <p className="mt-1 text-xs text-slate-400">{reason}{tier ? ` (requires BALLDONTLIE ${tier} tier).` : "."} No numbers are shown unless they are calculated from real game logs.</p>
      </div>
    </div>
  );
}
